//eslint-disable @typescript-eslint/no-explicit-any
import React, { useState, useRef, KeyboardEvent, memo } from 'react';
import { Controller } from 'react-hook-form';
import { 
  Chip, 
  Box, 
  FormControl, 
  FormHelperText,
  InputAdornment,
  styled
} from '@mui/material';
import Typography from '../typography/typography';

// interface ValidationRule {
//   type: 'email' | 'url' | 'phone' | 'custom';
//   pattern?: RegExp;
//   message: string;
//   validator?: (value: string) => boolean;
// }

interface ChipGeneratorProps {
  name: string;
  control: any;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  validation?: any[];
  maxChips?: number;
  allowDuplicates?: boolean;
  fieldArrayName?: string;
  endIcon?: React.ReactNode;
  helperText?: string;
  error?: boolean;
  onChipAdd?: (chip: string) => void;
  onChipRemove?: (chip: string, index: number) => void;
  customChipProps?: any;
  textFieldProps?: any;
  allowMultipleAdd?: boolean;
  delimiters?: string[];
  // New props for data type handling
  dataType?: 'array' | 'string';
  separator?: string;
}

// Styled components for inline chips
const ChipContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  minHeight: '1.4375em',
  border: `1px solid #D3D3D4`,
  borderRadius: '0.25rem',
  cursor: 'text',
  fontSize: '0.875rem',
//   '&:hover': {
//     borderColor: theme.palette.text.primary,
//   },
  '&.focused': {
    borderColor:'#54CC96',
    borderWidth: '1px',
  },
  '&.error': {
    borderColor: theme.palette.error.main,
  },
  '&.disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    cursor: 'default',
  },
  paddingLeft: 3  
}));

const InlineInput = styled('input')(({ theme }) => ({
  border: 'none',
  outline: 'none',
  backgroundColor: 'transparent',
  flexGrow: 1,
  minWidth: '120px',
  padding: '0.5rem',
  fontSize: '0.875rem',
  fontFamily: theme.typography.fontFamily,
  '&::placeholder': {
    color: theme.palette.text.secondary,
    opacity: 1,
  },
  '&:disabled': {
    cursor: 'default',
  }
}));

const InlineChip = styled(Chip)(({theme: { palette }}) => ({
  height: '24px',
  fontSize: '0.875rem',
  borderColor:' #54CC96',
  backgroundColor: palette.theme.primary[50],
  color:'#2EB273',
  margin: '1px',
  '& .MuiChip-deleteIcon': {
    fontSize: '16px',
    color:'#2EB273',
    '&:hover': {
      color: '#2EB273',
    },
  },
}));

const ChipGenerator: React.FC<ChipGeneratorProps> = memo(({
  name,
  control,
  label,
  placeholder = "Type and press space to add",
  required = false,
  disabled = false,
  validation,
  maxChips,
  allowDuplicates = false,
  fieldArrayName,
  endIcon,
  helperText,
  error: externalError,
  onChipAdd,
  onChipRemove,
  customChipProps = {},
  allowMultipleAdd = true,
  delimiters = [',', ';', '\n', '\t'],
  dataType = 'string',
  separator = ',',
}) => {
  const [inputValue, setInputValue] = useState('');
  const [validationError, setValidationError] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const validateChip = (value: string,validation: any): { isValid: boolean; error: string } => {
    if (!validation) return { isValid: true, error: '' };

    const trimmedValue = value.trim();
    if (!trimmedValue) return { isValid: false, error: 'Empty values are not allowed' };

    switch (validation.type) {
      case 'email': {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isValidEmail = emailRegex.test(trimmedValue);
        return {
          isValid: isValidEmail,
          error: isValidEmail ? '' : validation.message || 'Please enter a valid email address'
        };
      }

    //   case 'url': {
    //     const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/;
    //     const isValidUrl = urlRegex.test(trimmedValue);
    //     return {
    //       isValid: isValidUrl,
    //       error: isValidUrl ? '' : validation.message || 'Please enter a valid URL'
    //     };
    //   }

      case 'phone': {
        const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
        const isValidPhone = phoneRegex.test(trimmedValue.replace(/[\s()-]/g, ''));
        return {
          isValid: isValidPhone,
          error: isValidPhone ? '' : validation.message || 'Please enter a valid phone number'
        };
      }

      case 'custom': {
        if (validation.pattern) {
          const isValid = validation.pattern.test(trimmedValue);
          return {
            isValid,
            error: isValid ? '' : validation.message
          };
        }
        if (validation.validator) {
          const isValid = validation.validator(trimmedValue);
          return {
            isValid,
            error: isValid ? '' : validation.message
          };
        }
        return { isValid: true, error: '' };
      }

      default:
        return { isValid: true, error: '' };
    }
  };

  // Convert between string and array formats
  const stringToArray = (str: string): string[] => {
    if (!str || typeof str !== 'string') return [];
    return str.split(separator).map(item => item.trim()).filter(Boolean);
  };

  const arrayToString = (arr: string[]): string => {
    return arr.join(separator + ' ');
  };

  // Parse input value by delimiters
  const parseMultipleValues = (inputStr: string): string[] => {
    if (!allowMultipleAdd) {
      return [inputStr.trim()].filter(Boolean);
    }

    const delimiterPattern = delimiters.map(d => 
      d.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    ).join('|');
    
    const regex = new RegExp(`[${delimiterPattern}]`);
    
    return inputStr
      .split(regex)
      .map(val => val.trim())
      .filter(Boolean);
  };

  const addChips = (inputStr: string, currentChips: string[]): { 
    newChips: string[]; 
    errors: string[]; 
    addedCount: number;
  } => {
    const valuesToAdd = parseMultipleValues(inputStr);
    if (valuesToAdd.length === 0) {
      return { newChips: currentChips, errors: [], addedCount: 0 };
    }

    const newChips = [...currentChips];
    const errors: string[] = [];
    let addedCount = 0;

    for (const value of valuesToAdd) {
      if (maxChips && newChips.length >= maxChips) {
        errors.push(`Maximum ${maxChips} items allowed`);
        break;
      }
 
    if (validation?.length > 0) {
      const errorMessages = validation
        .map(val => validateChip(value, val))
        .filter(result => !result.isValid)
        .map(result => result.error);

      if (errorMessages.length > 0) {
        errors.push(`"${value}": ${errorMessages.join(', ')}`);
        continue;
      }
    }
   

      if (!allowDuplicates && newChips.includes(value)) {
        errors.push(`"${value}": Duplicate values are not allowed`);
        continue;
      }

      newChips.push(value);
      addedCount++;
      onChipAdd?.(value);
    }

    return { newChips, errors, addedCount };
  };

  const removeChip = (chipToRemove: string, index: number, currentChips: string[]) => {
    const newChips = currentChips.filter((_, i) => i !== index);
    onChipRemove?.(chipToRemove, index);
    setValidationError('');
    return newChips;
  };

  const processInput = (
    inputStr: string,
    onChange: (value: string[] | string) => void,
    currentValue: string[] | string
  ) => {
    const currentChips = Array.isArray(currentValue) ? currentValue : stringToArray(currentValue);
    const { newChips, errors } = addChips(inputStr, currentChips);
    
    if (errors.length > 0) {
      setValidationError(errors.join('; '));
    } else {
      setValidationError('');
    }
    
    if (newChips.length !== currentChips.length) {
      const outputValue = dataType === 'string' ? arrayToString(newChips) : newChips;
      onChange(outputValue);
      setInputValue('');
    }
  };

  const handleKeyPress = (
    event: KeyboardEvent<HTMLInputElement>,
    onChange: (value: string[] | string) => void,
    currentValue: string[] | string
  ) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      if (inputValue.trim()) {
        processInput(inputValue, onChange, currentValue);
      }
    } else if (event.key === 'Backspace' && !inputValue) {
      const currentChips = Array.isArray(currentValue) ? currentValue : stringToArray(currentValue);
      if (currentChips.length > 0) {
        const lastIndex = currentChips.length - 1;
        const newChips = removeChip(currentChips[lastIndex], lastIndex, currentChips);
        const outputValue = dataType === 'string' ? arrayToString(newChips) : newChips;
        onChange(outputValue);
      }
    }
  };

  const handleBlur = (
    onChange: (value: string[] | string) => void,
    currentValue: string[] | string
  ) => {
    setFocused(false);
    if (inputValue.trim()) {
      processInput(inputValue, onChange, currentValue);
    }
  };

  const handlePaste = (
    event: React.ClipboardEvent<HTMLInputElement>,
    onChange: (value: string[] | string) => void,
    currentValue: string[] | string
  ) => {
    if (allowMultipleAdd) {
      event.preventDefault();
      const pastedText = event.clipboardData.getData('text');
      const combinedInput = inputValue + pastedText;
      processInput(combinedInput, onChange, currentValue);
    }
  };

  const handleContainerClick = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const fieldName = fieldArrayName ? `${fieldArrayName}.${name}` : name;

  return (
    <Controller
      name={fieldName}
      control={control}
      defaultValue={dataType === 'string' ? '' : []}
      render={({ field: { onChange, value, ...field }, fieldState: { error } }) => {
        const currentChips = Array.isArray(value) ? value : stringToArray(value || '');
        const hasError = !!error || !!validationError || externalError;

        return (
          <FormControl fullWidth error={hasError}>
            {label && (
           <Typography
                   type="s5"
                   weight={"medium"}
                   mb={0.5}
                   color={error ? "theme.error.600" : "theme.secondary.800"}
                 >
                   {label} {required && <span className="requiredStar">*</span>}
                 </Typography>
            )}

            <ChipContainer
              ref={containerRef}
              className={`${focused ? 'focused' : ''} ${hasError ? 'error' : ''} ${disabled ? 'disabled' : ''}`}
              onClick={handleContainerClick}
            >
              {currentChips.map((chip: string, index: number) => (
                <InlineChip
                  key={`${chip}-${index}`}
                  label={chip}
                  onDelete={
                    disabled
                      ? undefined
                      : (event) => {
                          event.stopPropagation();
                          const newChips = removeChip(chip, index, currentChips);
                          const outputValue = dataType === 'string' ? arrayToString(newChips) : newChips;
                          onChange(outputValue);
                        }
                  }
                  size="small"
                  // color="primary"
                  // variant="outlined"
                  {...customChipProps}
                />
              ))}
              
              <InlineInput
                {...field}
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => handleKeyPress(e, onChange, value)}
                onFocus={() => setFocused(true)}
                onBlur={() => handleBlur(onChange, value)}
                onPaste={(e) => handlePaste(e, onChange, value)}
                placeholder={currentChips.length === 0 ? placeholder : ''}
                disabled={disabled}
                style={{ 
                  width: inputValue ? `${Math.max(inputValue.length * 8, 120)}px` : '120px',
                }}
              />
              
              {endIcon && (
                <InputAdornment position="end" sx={{ ml: 1 }}>
                  {endIcon}
                </InputAdornment>
              )}
            </ChipContainer>

            {(helperText || error?.message || validationError) && (
              <FormHelperText>
                {error?.message || validationError || helperText}
              </FormHelperText>
            )}

            {maxChips && (
              <FormHelperText>
                {currentChips.length} / {maxChips} items
              </FormHelperText>
            )}

          
          </FormControl>
        );
      }}
    />
  );
});

export default ChipGenerator;